import React, { useState } from "react";
// Mesaj veri tipi tanımı
interface Message {
  id: number;
  platform: "email" | "whatsapp" | "facebook" | "instagram" | "twitter" | "linkedin";
  sender: string;
  subject: string;
  content: string;
  timestamp: string;
  status: "unread" | "read" | "replied";
  avatar?: string;
}

// Platform ikonları için yardımcı fonksiyon
const getPlatformIcon = (platform: string): string => {
  const icons: Record<string, string> = {
    email: "bi-envelope-fill",
    whatsapp: "bi-whatsapp",
    facebook: "bi-facebook",
    instagram: "bi-instagram",
    twitter: "bi-twitter",
    linkedin: "bi-linkedin",
  };
  return icons[platform] || "bi-chat-dots-fill";
};

// Platform renkleri için yardımcı fonksiyon
const getPlatformColor = (platform: string): string => {
  const colors: Record<string, string> = {
    email: "rgba(234, 67, 53, 0.9)",
    whatsapp: "rgba(37, 211, 102, 0.9)",
    facebook: "rgba(24, 119, 242, 0.9)",
    instagram: "rgba(228, 64, 95, 0.9)",
    twitter: "rgba(29, 161, 242, 0.9)",
    linkedin: "rgba(10, 102, 194, 0.9)",
  };
  return colors[platform] || "#6c757d";
};

// Durum rozetleri için yardımcı fonksiyon
const getStatusBadgeClass = (status: string): string => {
  const classes: Record<string, string> = {
    unread: "bg-danger",
    read: "bg-secondary",
    replied: "bg-success",
  };
  return classes[status] || "bg-secondary";
};

const CommunicationsHubPage: React.FC = () => {
  // Örnek veri
  const dummyMessages: Message[] = [
    {
      id: 1,
      platform: "email",
      sender: "john.doe@example.com",
      subject: "Marketing Campaign Proposal",
      content: "Hi, I would like to discuss our upcoming marketing campaign. Can we schedule a call?",
      timestamp: "2024-03-20T10:30:00",
      status: "unread",
    },
    {
      id: 2,
      platform: "whatsapp",
      sender: "+1 234 567 8900",
      subject: "Product Inquiry",
      content: "Hello! I saw your product on Instagram. Can you tell me more about the pricing?",
      timestamp: "2024-03-20T09:15:00",
      status: "read",
    },
    {
      id: 3,
      platform: "facebook",
      sender: "Sarah Johnson",
      subject: "Customer Feedback",
      content: "Great service! Would love to see more content about your new features.",
      timestamp: "2024-03-19T15:45:00",
      status: "replied",
    },
    {
      id: 4,
      platform: "instagram",
      sender: "@tech_enthusiast",
      subject: "Collaboration Request",
      content: "Hey! Love your content. Would you be interested in a collaboration?",
      timestamp: "2024-03-19T14:20:00",
      status: "unread",
    },
    {
      id: 5,
      platform: "twitter",
      sender: "@marketing_pro",
      subject: "Industry Question",
      content: "What are your thoughts on the latest marketing trends?",
      timestamp: "2024-03-19T11:30:00",
      status: "read",
    },
    {
      id: 6,
      platform: "linkedin",
      sender: "Michael Brown",
      subject: "Business Partnership",
      content: "I think our companies could benefit from a strategic partnership.",
      timestamp: "2024-03-18T16:15:00",
      status: "unread",
    },
  ];

  // State tanımları
  const [messages, setMessages] = useState<Message[]>(dummyMessages);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyContent, setReplyContent] = useState("");

  // Filtreleme işlemi
  const filteredMessages = messages.filter((message) => {
    const matchesSearch =
      message.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.content.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlatform = selectedPlatform === "all" || message.platform === selectedPlatform;
    const matchesStatus = selectedStatus === "all" || message.status === selectedStatus;

    return matchesSearch && matchesPlatform && matchesStatus;
  });

  // Yanıtlama işlemleri
  const handleReply = (message: Message) => {
    setSelectedMessage(message);
    setReplyDialogOpen(true);
  };

  const handleSendReply = () => {
    if (selectedMessage && replyContent.trim()) {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedMessage.id ? { ...msg, status: "replied" } : msg
        )
      );
      setReplyDialogOpen(false);
      setReplyContent("");
      setSelectedMessage(null);
    }
  };

  const handleGenerateAIResponse = () => {
    // Yapay zeka yanıtı simülasyonu
    const aiResponse = `Thank you for your message regarding "${selectedMessage?.subject}". I'd be happy to help you with that. Let's schedule a call to discuss this further. Would you be available for a 30-minute meeting this week?`;
    setReplyContent(aiResponse);
  };

  // Filtreleri sıfırlama
  const resetFilters = () => {
    setSelectedPlatform("all");
    setSelectedStatus("all");
    setSearchTerm("");
  };

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="p-4 mb-4 rounded-4 bg-opacity-10 bg-primary shadow-sm" 
           style={{ background: "linear-gradient(135deg, rgba(65, 88, 208, 0.1) 0%, rgba(200, 80, 192, 0.1) 100%)" }}>
        <h1 className="fw-bold mb-2" 
            style={{ background: "linear-gradient(135deg, #4158D0 0%, #C850C0 50%, #FFCC70 100%)", 
                    WebkitBackgroundClip: "text", 
                    WebkitTextFillColor: "transparent" }}>
          Communications Hub
        </h1>
        <p className="text-muted mb-0">
          Manage all your communications across different platforms in one place
        </p>
      </div>

      {/* Filtre Araç Çubuğu */}
      <div className="card mb-4 border-0 shadow-sm rounded-4 bg-opacity-10 bg-light">
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-transparent border-end-0">
                  <i className="bi bi-search text-primary"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
              >
                <option value="all">All Platforms</option>
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
              </select>
            </div>
            <div className="col-12 col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={resetFilters}
              >
                <i className="bi bi-funnel me-2"></i>
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mesaj Listesi */}
      <div className="d-flex flex-column gap-3">
        {filteredMessages.length > 0 ? (
          filteredMessages.map((message) => (
            <div
              key={message.id}
              className="card border-0 shadow-sm rounded-4 position-relative overflow-hidden"
              style={{ transition: "all 0.3s ease" }}
            >
              <div
                className="position-absolute top-0 bottom-0 start-0"
                style={{ width: "4px", background: getPlatformColor(message.platform) }}
              ></div>
              <div className="card-body p-3">
                <div className="row align-items-center">
                  <div className="col-12 col-md-1">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "48px",
                        height: "48px",
                        background: getPlatformColor(message.platform),
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <i className={`bi ${getPlatformIcon(message.platform)} fs-5 text-white`}></i>
                    </div>
                  </div>
                  <div className="col-12 col-md-3">
                    <h6 className="mb-1 fw-bold" style={{ background: "linear-gradient(135deg, #4158D0 0%, #C850C0 50%, #FFCC70 100%)", 
                                                         WebkitBackgroundClip: "text", 
                                                         WebkitTextFillColor: "transparent" }}>
                      {message.sender}
                    </h6>
                    <small className="text-muted">
                      {new Date(message.timestamp).toLocaleString()}
                    </small>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="fw-semibold mb-1">{message.subject}</div>
                    <p className="text-muted mb-0 small message-content" style={{ opacity: 0.8 }}>
                      {message.content}
                    </p>
                  </div>
                  <div className="col-12 col-md-2">
                    <span className={`badge ${getStatusBadgeClass(message.status)} text-capitalize`}>
                      {message.status}
                    </span>
                  </div>
                  <div className="col-12 col-md-2">
                    <button
                      className="btn btn-primary w-100 rounded-3"
                      style={{ 
                        background: "linear-gradient(135deg, #4158D0 0%, #C850C0 50%, #FFCC70 100%)",
                        border: "none"
                      }}
                      onClick={() => handleReply(message)}
                    >
                      <i className="bi bi-reply me-2"></i>
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="alert alert-info">No messages found matching your filters.</div>
        )}
      </div>

      {/* Yanıt Modal */}
      {replyDialogOpen && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header bg-light bg-opacity-50 border-bottom">
                <div>
                  <h5 className="modal-title text-primary fw-bold">
                    Reply to {selectedMessage?.sender}
                  </h5>
                  <div className="text-muted small mt-1">
                    {selectedMessage?.subject}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setReplyDialogOpen(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="bg-light bg-opacity-50 p-3 rounded-3 mb-3">
                  <div className="text-muted small mb-2">Original Message:</div>
                  <div>{selectedMessage?.content}</div>
                </div>
                <hr />
                <div className="mb-3">
                  <label htmlFor="replyContent" className="form-label">Your Reply</label>
                  <textarea
                    id="replyContent"
                    className="form-control"
                    rows={4}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                  ></textarea>
                </div>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleGenerateAIResponse}
                >
                  <i className="bi bi-robot me-2"></i>
                  Generate Response with AI
                </button>
              </div>
              <div className="modal-footer bg-light bg-opacity-50 border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setReplyDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendReply}
                  disabled={!replyContent.trim()}
                >
                  Send Reply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationsHubPage;
